import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { SEARCH_MODULES, isModuleAvailable } from './globalSearchModules';

// Alt + a number opens a module from any page, in the order of the home screen — Alt+1 AI photo, Alt+2 bulk photo,
// Alt+3 AI documents, Alt+4 AI template, Alt+5 print media, Alt+6 SohozTools, then Alt+7 manual photo, Alt+8 document,
// Alt+9 My Files, Alt+0 home. The numbers live in globalSearchModules.js (the shortcuts window lists them from there too).
//
// Only for a signed-in owner/operator — the accounts that have these pages — and never while a popup or the photo eraser
// is open, so a stray chord can't pull someone out of what they are doing. Matching uses `e.code` (the physical key on the
// number row), so it behaves the same on a Bangla keyboard layout; Ctrl/Shift/AltGr combinations are left alone.

// A shadcn dialog / drawer (role="dialog", and it scroll-locks the page). Not "the page can't scroll": some pages (bulk photo
// edit) keep their body scroll-locked all the time.
export const popupOpen = () =>
    !!document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')
    || document.body.hasAttribute('data-scroll-locked');

export default function ModuleShortcuts() {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuthStore();
    const allowed = isAuthenticated && (user?.role === 'OWNER' || user?.role === 'OPERATOR');
    const isBdUser = !user?.country || String(user.country).toUpperCase() === 'BD';
    const isOwner = user?.role === 'OWNER';

    useEffect(() => {
        if (!allowed) return undefined;

        const byCode = new Map(
            SEARCH_MODULES
                .filter((m) => m.shortcut && isModuleAvailable(m, { isBdUser, isOwner }))
                .map((m) => [`Digit${m.shortcut}`, m])
        );

        // The photo eraser has keys of its own and is not a dialog — it announces itself (SupportWidget listens too).
        let eraserOpen = false;
        const onEraser = (e) => { eraserOpen = e.type === 'eraser-modal-open'; };

        const onKeyDown = (e) => {
            if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.repeat || e.isComposing) return;
            if (e.getModifierState?.('AltGraph')) return;
            const module = byCode.get(e.code);
            if (!module || eraserOpen || popupOpen()) return;
            e.preventDefault();                       // keep the browser's own Alt+key out of it …
            e.stopImmediatePropagation();             // … and page-level key handlers (e.g. the template list's row-number keys)
            if (window.location.pathname !== module.to) navigate(module.to);
        };

        // Capture phase: the chord is ours before any page handler sees it.
        window.addEventListener('keydown', onKeyDown, true);
        window.addEventListener('eraser-modal-open', onEraser);
        window.addEventListener('eraser-modal-close', onEraser);
        return () => {
            window.removeEventListener('keydown', onKeyDown, true);
            window.removeEventListener('eraser-modal-open', onEraser);
            window.removeEventListener('eraser-modal-close', onEraser);
        };
    }, [allowed, isBdUser, isOwner, navigate]);

    return null;
}
